import { createSlice } from "@reduxjs/toolkit";
import {
  CreateProjectMenu,
  GetProjectMenu,
  updateProjectMenuItem,
  updateMenuCustomFieldValue,
  ToggleProjectMenuItems,
  GetProjectMenuByUniqueKey,
  updateMenuItemByUniqueKey,
  updateMenuCustomFieldValueByUniqueKey,
  updateMenuStatusByUniqueKey,
  GetMenuDocument,
} from "../thunks/projectMenuThunks";
import {
  ProjectMenuDTO,
  CreateProjectMenuResponse,
  CreateProjectMenuRequest,
  ProjectMenuRequestDTO,
} from "../../models/projectMenu.model";

export interface ProjectMenuState {
  projectMenuRequest: ProjectMenuRequestDTO;
  projectMenu: ProjectMenuDTO;
  createProjectMenuResponse: CreateProjectMenuResponse;
  createProjectMenuRequest: CreateProjectMenuRequest;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  downloadLoading: boolean;
  downloadError: string | null;
  downloadSuccess: boolean;
}

const initialState: ProjectMenuState = {
  projectMenu: {} as ProjectMenuDTO,
  projectMenuRequest: {} as ProjectMenuRequestDTO,
  createProjectMenuResponse: {} as CreateProjectMenuResponse,
  createProjectMenuRequest: {} as CreateProjectMenuRequest,
  loading: false,
  error: null,
  initialized: false,
  downloadLoading: false,
  downloadError: null,
  downloadSuccess: false,
};

const projectMenuSlice = createSlice({
  name: "projectMenu",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDownloadMessages: (state) => {
      state.downloadError = null;
      state.downloadSuccess = false;
    },
    clearProjectMenu: (state) => {
      state.projectMenu = {} as ProjectMenuDTO;
      state.initialized = false;
      state.error = null;
      state.loading = false;
    },
    clearProjectMenuRequest: (state) => {
      state.projectMenuRequest = {} as ProjectMenuRequestDTO;
      state.initialized = false;
      state.error = null;
      state.loading = false;
    },
    setProjectMenuRequest: (state, action) => {
      state.projectMenuRequest = action.payload;
    },
    setProjectMenu: (state, action) => {
      state.projectMenu = action.payload;
      state.initialized = true;
    },

    updateCustomFieldValue: (state, action) => {
      const { fieldId, value } = action.payload;
      if (state.projectMenu.customFields) {
        const field = state.projectMenu.customFields.find(
          (f) => f.menuCustomField_ID === fieldId,
        );
        if (field) {
          field.fieldValue = String(value);
        }
      }
    },
    toggleItemLocally: (state, action) => {
      const { categoryId, itemId, itemName } = action.payload;
      const category = state.projectMenu.categories?.find(
        (cat) => cat.baseMenuCategory_ID === categoryId,
      );
      if (category) {
        // אם projectMenuItem_ID הוא 0, נחפש לפי שם המנה
        let item;
        if (itemId === 0) {
          item = category.items?.find((i) => i.baseMenuItem_Name === itemName);
        } else {
          item = category.items?.find((i) => i.projectMenuItem_ID === itemId);
        }

        if (item) {
          const _oldValue = item.selected;
          item.selected = !item.selected;
        } else {
          console.log("Item not found");
        }
      } else {
        console.log("Category not found with ID:", categoryId);
      }
    },
    updateItemComment: (state, action) => {
      const { categoryId, itemId, itemName, comment, qtyPercent } =
        action.payload;

      const category = state.projectMenu.categories?.find(
        (cat) => cat.baseMenuCategory_ID === categoryId,
      );

      if (category) {
        let item;
        if (itemId === 0) {
          item = category.items?.find((i) => i.baseMenuItem_Name === itemName);
        } else {
          item = category.items?.find((i) => i.projectMenuItem_ID === itemId);
        }

        if (item) {
          item.projectMenuItem_Comments = comment;
          if (qtyPercent !== undefined) {
            item.qtyPercent = qtyPercent;
          }
        }
      }
    },
    toggleCategoryItems: (state, action) => {
      const { categoryId, selectStatus } = action.payload;

      const category = state.projectMenu.categories?.find(
        (cat) => cat.baseMenuCategory_ID === categoryId,
      );

      if (category && category.items) {
        category.items.forEach((item) => {
          item.selected = selectStatus;
        });
      }
    },
    // ✅ Toggle מקומי לקומפוננטה חיצונית
    toggleItemLocallyByUniqueKey: (state, action) => {
      const { categoryId, itemId } = action.payload;

      const category = state.projectMenuRequest.categories?.find(
        (cat) => cat.baseMenuCategory_ID === categoryId,
      );

      if (category) {
        const item = category.items?.find((i) => i.baseMenuItem_ID === itemId);
        if (item) {
          item.selected = !item.selected;
        }
      }
    },

    // ✅ עדכון שדה מותאם מקומי
    updateCustomFieldValueLocallyByUniqueKey: (state, action) => {
      const { fieldId, value } = action.payload;

      if (state.projectMenuRequest.customFields) {
        const field = state.projectMenuRequest.customFields.find(
          (f) => f.menuCustomField_ID === fieldId,
        );
        if (field) {
          field.fieldValue = String(value);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetProjectMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetProjectMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.projectMenu = action.payload.projectMenuDTO;
        state.initialized = true;
      })
      .addCase(GetProjectMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch project menu";
      })
      .addCase(CreateProjectMenu.pending, (state) => {
        // state.loading = true;
        state.error = null;
      })
      .addCase(CreateProjectMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.createProjectMenuResponse = action.payload;
        state.createProjectMenuRequest.baseMenuID =
          state.createProjectMenuResponse.projectMenuID;
        state.initialized = true;

        // עדכון התפריט לאחר הצלחה - טעינה מחדש
        // זה יבטיח שהנתונים מעודכנים מהשרת
      })
      .addCase(CreateProjectMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create project menu";
      })
      .addCase(updateProjectMenuItem.pending, (_state) => {
        // לא מציגים loading עבור עדכון פריטים בודדים
      })
      .addCase(updateProjectMenuItem.fulfilled, (_state, _action) => {
        // העדכון כבר בוצע באופן אופטימיסטי
      })
      .addCase(updateProjectMenuItem.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to update menu item";
        // החזרת השינוי במקרה של כשלון
      })
      .addCase(updateMenuCustomFieldValue.pending, (_state) => {
        // לא מציגים loading עבור עדכון שדות מותאמים
      })
      .addCase(updateMenuCustomFieldValue.fulfilled, (_state, _action) => {
        // העדכון כבר בוצע באופן מקומי
      })
      .addCase(updateMenuCustomFieldValue.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to update custom field value";
      })
      .addCase(ToggleProjectMenuItems.pending, (_state) => {
        // לא מציגים loading עבור toggle
      })
      .addCase(ToggleProjectMenuItems.fulfilled, (_state, _action) => {
        // העדכון כבר בוצע באופן אופטימיסטי
      })
      .addCase(ToggleProjectMenuItems.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to toggle menu items";
      })
      .addCase(GetProjectMenuByUniqueKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetProjectMenuByUniqueKey.fulfilled, (state, action) => {
        state.loading = false;
        state.projectMenuRequest = action.payload.projectMenuDTO;
        state.initialized = true;
      })
      .addCase(GetProjectMenuByUniqueKey.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch project menu by unique key";
      })
      // ✅ Update Item By UniqueKey
      .addCase(updateMenuItemByUniqueKey.pending, (_state) => {
        // אופטימיסטי - לא צריך loading
      })
      .addCase(updateMenuItemByUniqueKey.fulfilled, (_state, _action) => {
        // העדכון כבר בוצע מקומית
      })
      .addCase(updateMenuItemByUniqueKey.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to update menu item";
      })

      // ✅ Update Custom Field By UniqueKey
      .addCase(updateMenuCustomFieldValueByUniqueKey.pending, (_state) => {
        // אופטימיסטי - לא צריך loading
      })
      .addCase(updateMenuCustomFieldValueByUniqueKey.fulfilled, (_state) => {
        // העדכון כבר בוצע מקומית
      })
      .addCase(
        updateMenuCustomFieldValueByUniqueKey.rejected,
        (state, action) => {
          state.error =
            (action.payload as string) || "Failed to update custom field";
        },
      )

      // ✅ Update Status By UniqueKey
      .addCase(updateMenuStatusByUniqueKey.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateMenuStatusByUniqueKey.fulfilled, (state, _action) => {
        state.loading = false;
        // עדכון הסטטוס בתפריט
        if (state.projectMenuRequest.projectMenuHeader_Status !== undefined) {
          // משנה לסטטוס "ממתין לאישור" (2)
          state.projectMenuRequest.projectMenuHeader_Status = 2;
          state.projectMenuRequest.projectMenuHeader_StatusName =
            "ממתין לאישור";
          state.projectMenuRequest.projectMenuHeader_IsEditable = false;
        }
      })
      .addCase(updateMenuStatusByUniqueKey.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to update status";
      })
      .addCase(GetMenuDocument.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
        state.downloadSuccess = false;
      })
      .addCase(GetMenuDocument.fulfilled, (state) => {
        state.downloadLoading = false;
        state.downloadSuccess = true;
      })
      .addCase(GetMenuDocument.rejected, (state, _action) => {
        state.downloadLoading = false;
        state.downloadError =
          (_action.payload as string) || "Failed to get document";
        state.downloadSuccess = false;
      });
  },
});

export const {
  clearError,
  clearDownloadMessages,
  clearProjectMenu,
  updateCustomFieldValue,
  toggleItemLocally,
  updateItemComment,
  toggleCategoryItems,
  toggleItemLocallyByUniqueKey,
  updateCustomFieldValueLocallyByUniqueKey,
} = projectMenuSlice.actions;

export default projectMenuSlice.reducer;
